import type { LiveTranscript } from '@karehero/models';
import { words } from '@karehero/helpers/string';

/** Fewer words than this is too little to read in place of the audio. */
export const MIN_TRANSCRIPT_WORDS = 3;

/** Speech runs at two or three words a second; well below this, words were lost. */
export const MIN_WORDS_PER_SECOND = 0.5;

// A number is said a digit at a time, so each digit counts as a word.
const wordsIn = (text: string) =>
  words(text).reduce((count, word) => {
    const digits = word.replace(/\D/g, '').length;
    return count + Math.max(1, digits);
  }, 0);

/** Everything heard, with any words the provider had not yet settled on. */
export const heardText = ({ text, interimText }: LiveTranscript) =>
  [text, interimText]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');

/** The transcript to read instead of the recording, or null when too thin to trust. */
export const usableTranscript = (
  transcript: LiveTranscript,
  hasFailed: boolean,
  durationSeconds: number,
): string | null => {
  if (hasFailed) return null;
  const heard = heardText(transcript);
  const words = wordsIn(heard);
  if (words < MIN_TRANSCRIPT_WORDS) return null;
  if (words < durationSeconds * MIN_WORDS_PER_SECOND) return null;
  return heard;
};
